import type {Shift} from "@/src/modules/shifts/types";

/** One row in `Shift.company_vehicle` (ordered; last = in use). */
export type ShiftCompanyVehicleRef = {
  id: number;
  /** Legacy display / fallback when plate + make + model are absent. */
  name?: string | null;
  unit_number?: string | null;
  plate?: string | null;
  make?: string | null;
  model?: string | null;
};

function str(v: unknown): string | null | undefined {
  if (v == null) return v as null | undefined;
  if (typeof v === "string") return v;
  if (typeof v === "number" && Number.isFinite(v)) return String(v);
  return undefined;
}

function normalizeOne(raw: unknown): ShiftCompanyVehicleRef | null {
  if (raw == null || typeof raw !== "object") return null;
  const o = raw as {
    id?: unknown;
    name?: string | null;
    label?: string | null;
    unit_number?: string | null;
    plate?: string | null;
    make?: string | null;
    model?: string | null;
  };
  if (o.id == null || !Number.isFinite(Number(o.id))) return null;
  return {
    id: Number(o.id),
    name: o.name ?? o.label,
    unit_number: str(o.unit_number),
    plate: str(o.plate),
    make: str(o.make),
    model: str(o.model),
  };
}

/**
 * List of company vehicles on a shift. API shape is always an array; supports legacy
 * single-object `{ id, name }` during migration.
 */
export function listShiftCompanyVehicles(shift: Shift | null | undefined): ShiftCompanyVehicleRef[] {
  if (!shift) return [];
  const raw =
    (shift as {companyVehicles?: unknown}).companyVehicles ?? shift.company_vehicle;
  if (raw == null) return [];
  if (Array.isArray(raw)) {
    return raw.map(normalizeOne).filter((v): v is ShiftCompanyVehicleRef => v != null);
  }
  const one = normalizeOne(raw);
  return one ? [one] : [];
}

/** Currently held vehicle: last in API order (first start checks → swaps). */
export function getCurrentShiftCompanyVehicle(
  shift: Shift | null | undefined,
): ShiftCompanyVehicleRef | null {
  const list = listShiftCompanyVehicles(shift);
  if (list.length === 0) return null;
  return list[list.length - 1] ?? null;
}

function toStableRow(r: ShiftCompanyVehicleRef): ShiftCompanyVehicleRef {
  const nameFallback = (r.name ?? `Vehicle #${r.id}`).toString();
  return {...r, name: nameFallback};
}

/**
 * After a successful start-phase POST, build the new `company_vehicle` list (optimistic).
 * Appends when the new custody id differs from the last entry; otherwise returns the previous list.
 */
export function mergeShiftCompanyVehiclesAfterStart(
  shift: Shift,
  custody: ShiftCompanyVehicleRef,
): ShiftCompanyVehicleRef[] {
  const prev = listShiftCompanyVehicles(shift);
  const last = prev[prev.length - 1];
  if (last && last.id === custody.id) {
    return prev.map(toStableRow);
  }
  return [...prev.map(toStableRow), toStableRow(custody)];
}
